import { alpha, createTheme } from '@mui/material/styles'
// Teaches `createTheme` that MuiDataGrid is a real slot. Type-only, so it
// disappears at build time — without it the grid overrides below are a
// compile error rather than a silent no-op.
import type {} from '@mui/x-data-grid/themeAugmentation'

/**
 * The warm palette from v1, as MUI tokens.
 *
 * Carried over rather than restarted: the colours were the one part of the
 * old UI nobody complained about. What was wrong was the geometry and the
 * scale — "the alignment feels dated", "compared to gmail our fonts are big".
 * So the hues survive and every measurement here is tighter than MUI's
 * defaults.
 */
const warm = {
  bg: '#FDFBF7',
  surface: '#F6F1E8',
  field: '#FFFFFF',
  ink: '#2A2622',
  muted: '#8A8078',
  line: '#E8DFD2',
  accent: '#C2542B',
  accentFg: '#FFFBF6',
  ok: '#4A7C59',
  warn: '#B8763A',
  danger: '#A8352A',
} as const

export const tokens = warm

export const theme = createTheme({
  palette: {
    mode: 'light',
    primary: { main: warm.accent, contrastText: warm.accentFg },
    success: { main: warm.ok },
    warning: { main: warm.warn },
    error: { main: warm.danger },
    background: { default: warm.bg, paper: warm.field },
    text: { primary: warm.ink, secondary: warm.muted },
    divider: warm.line,
  },

  shape: { borderRadius: 10 },

  typography: {
    fontFamily: [
      '"Inter Variable"', 'Inter', '-apple-system', 'BlinkMacSystemFont',
      '"Segoe UI"', 'Roboto', 'sans-serif',
    ].join(','),
    // 14px base, not MUI's 16. Gmail sits at 14 and this is the same kind of
    // screen: a dense list somebody scans rather than a page they read.
    fontSize: 14,
    htmlFontSize: 16,
    h1: { fontSize: '1.5rem', fontWeight: 650, letterSpacing: '-0.02em' },
    h2: { fontSize: '1.1875rem', fontWeight: 650, letterSpacing: '-0.015em' },
    h3: { fontSize: '1rem', fontWeight: 620, letterSpacing: '-0.01em' },
    body1: { fontSize: '0.875rem', lineHeight: 1.5 },
    body2: { fontSize: '0.8125rem', lineHeight: 1.45 },
    button: { textTransform: 'none', fontWeight: 560, letterSpacing: 0 },
    caption: { fontSize: '0.75rem', color: warm.muted },
  },

  // Flat. Depth here comes from a hairline and a tint, never a drop shadow —
  // stacked shadows are most of what reads as "dated".
  shadows: Array(25).fill('none') as never,

  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: { backgroundColor: warm.bg, color: warm.ink },
        // Always visible, never overlay-on-hover. "The scrollbar is not
        // visible by default" was a real complaint: a list that scrolls must
        // look like it scrolls.
        '*::-webkit-scrollbar': { width: 11, height: 11 },
        '*::-webkit-scrollbar-thumb': {
          backgroundColor: alpha(warm.ink, 0.22),
          borderRadius: 6,
          border: `3px solid ${warm.bg}`,
        },
        '*::-webkit-scrollbar-thumb:hover': {
          backgroundColor: alpha(warm.ink, 0.34),
        },
        // The example text goes when you start typing in the box, not when
        // you have finished reading it.
        //
        // Placeholders here are instructions rather than labels — the add
        // box shows four lines of what the parser accepts — and left visible
        // under a cursor they read as content somebody has to delete. Fading
        // rather than cutting, so a glance away and back does not flicker.
        //
        // Done in CSS rather than by swapping the attribute on focus,
        // because half these fields already use onBlur to save what was
        // typed and a hook owning that handler would fight them.
        // Both forms on purpose. The bare element covers anything not wrapped
        // in an MUI input; the class-qualified one outranks Emotion's own
        // `.css-hash::placeholder`, which sets opacity and would otherwise
        // make this a coin-toss decided by stylesheet injection order.
        'input::placeholder, textarea::placeholder, .MuiInputBase-input::placeholder':
          { transition: 'opacity 120ms ease' },
        'input:focus::placeholder, textarea:focus::placeholder, .MuiInputBase-input:focus::placeholder':
          { opacity: 0 },
      },
    },

    MuiButton: {
      defaultProps: { disableElevation: true, size: 'small' },
      styleOverrides: {
        root: { borderRadius: 9, paddingInline: 14, minHeight: 32 },
      },
    },

    MuiTextField: {
      defaultProps: { size: 'small', variant: 'outlined' },
    },

    MuiInputLabel: {
      // Label above and to the left in a smaller font, as asked for. The
      // shrunk-and-notched label is the single most dated thing MUI ships.
      defaultProps: { shrink: true },
      styleOverrides: {
        root: {
          position: 'static',
          transform: 'none',
          fontSize: '0.75rem',
          fontWeight: 560,
          color: warm.muted,
          marginBottom: 4,
          '&.Mui-focused': { color: warm.muted },
        },
      },
    },

    MuiOutlinedInput: {
      styleOverrides: {
        root: { borderRadius: 9, backgroundColor: warm.field },
        notchedOutline: { top: 0, '& legend': { display: 'none' } },
        input: { paddingBlock: 7, fontSize: '0.875rem' },
      },
    },

    MuiPaper: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: { backgroundImage: 'none', border: `1px solid ${warm.line}` },
      },
    },

    MuiChip: {
      styleOverrides: {
        root: { borderRadius: 6, fontWeight: 560, fontSize: '0.75rem', height: 22 },
      },
    },

    MuiTooltip: {
      defaultProps: { arrow: true, enterDelay: 300 },
      styleOverrides: {
        tooltip: {
          backgroundColor: warm.ink,
          fontSize: '0.75rem',
          fontWeight: 450,
          lineHeight: 1.4,
          padding: '6px 9px',
          maxWidth: 260,
        },
        arrow: { color: warm.ink },
      },
    },

    MuiDataGrid: {
      styleOverrides: {
        root: {
          border: `1px solid ${warm.line}`,
          borderRadius: 10,
          backgroundColor: warm.field,
          '--DataGrid-rowBorderColor': warm.line,
        },
        columnHeaders: { backgroundColor: warm.surface },
        columnHeaderTitle: {
          fontSize: '0.75rem',
          fontWeight: 620,
          letterSpacing: '0.01em',
          color: warm.muted,
        },
        cell: { fontSize: '0.8125rem', borderColor: warm.line },
        footerContainer: { minHeight: 40, borderColor: warm.line },
      },
    },
  },
})
